import React, { useRef } from 'react';
import { Box, Flex, Link, Text } from '@opendoor/bricks/core';
import { getHumanReadableAddress } from '../helpers/property';
import type { MapInnerProps } from '../../shared/MapInner';
import { DynamicComponentWithCondition } from 'components/shared/dynamic';
import { useInViewAfterLoad } from 'components/shared/hooks/useInView';

interface MapViewProps {
  latitude?: number | null;
  longitude?: number | null;
  street?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  neighborhood?: {
    href: string;
    name: string;
  };
  buyerAgentPDP?: boolean;
}

const MapView: React.FC<MapViewProps> = ({
  latitude,
  longitude,
  street,
  city,
  state,
  postalCode,
  neighborhood,
  buyerAgentPDP,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const { inView: inViewMapPage } = useInViewAfterLoad({
    triggerOnce: true,
    initialInView: false,
    ref,
  });
  const humanReadableAddress = getHumanReadableAddress(street, city, state, postalCode);
  return (
    <>
      <Flex ref={ref} justifyContent="space-between" alignItems="center">
        <Text as="h2" fontSize="s3" fontWeight="bold">
          Map
        </Text>
        <Link
          fontSize="s0"
          target="_blank"
          color="neutrals100"
          rel="noopener noreferrer"
          textDecoration="underline"
          aria-label={`View ${humanReadableAddress} in Google Maps`}
          href={`https://maps.google.com/maps?q=${encodeURIComponent(humanReadableAddress)}`}
          analyticsName="cosmos-pdp-home-details-google-maps"
        >
          See in Google Maps
        </Link>
      </Flex>
      {latitude && longitude && (
        <Box h="265px" position="relative" mt={[3, 4]} borderRadius="semiRounded" overflow="hidden">
          <DynamicComponentWithCondition<MapInnerProps>
            dynamicLoaderFunction={() => import('../../shared/MapInner').then((mod) => mod.default)}
            loadingCondition={inViewMapPage}
            latitude={latitude}
            longitude={longitude}
          />
        </Box>
      )}
      {neighborhood && !buyerAgentPDP && (
        <Box mt={[6, 7]}>
          <Link
            fontWeight="semibold"
            href={neighborhood.href}
            aria-label={`See more listings in ${neighborhood.name}`}
            analyticsName="cosmos-pdp-home-details-market-listings"
          >
            See more listings in {neighborhood.name}
          </Link>
        </Box>
      )}
    </>
  );
};

export default MapView;
